import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { resolveImageUrl } from "@/components/forms/ImageUploader";

/**
 * HomeBanner — promotional advertisement slot on the home page.
 *
 * Placed between the "Premium Spas" carousel and the "Popular Areas" section.
 * The banner is fully admin-controlled via `/admin/home-banner`:
 *   - If disabled or missing an image → this component renders `null` so no
 *     empty space appears on the page.
 *   - If a `link_url` is configured → the entire banner is clickable and
 *     opens in the configured target (same tab / new tab).
 *   - Uses native lazy-loading + async decoding to avoid blocking first paint.
 *
 * Design intent:
 *   - Full width within the same 6xl page container as neighbouring sections
 *     (consistent gutters).
 *   - Rounded 2xl corners to match spa cards + area tiles.
 *   - `aspect-[4.8/1]` locks the crop to the recommended 1440×300 canvas —
 *     `object-cover` guarantees no stretching or distortion at any viewport.
 *   - `section-wash` styling deliberately avoided so the banner "floats" on
 *     the page background rather than looking like a container of its own.
 */
export default function HomeBanner() {
  const [banner, setBanner] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/banners/home")
      .then((r) => {
        if (!cancelled) setBanner(r.data.banner || null);
      })
      .catch(() => {
        if (!cancelled) setBanner(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Suppress the section entirely until we know whether a banner exists —
  // avoids a layout jump if one is configured, and hides cleanly if not.
  if (!loaded || !banner || !banner.image_url) return null;

  const imgSrc = resolveImageUrl(banner.image_url);
  const alt = banner.alt_text || "Sponsored banner";
  const linkUrl = banner.link_url && banner.link_url.trim();
  const target = banner.target === "_blank" ? "_blank" : "_self";
  const rel = target === "_blank" ? "noopener noreferrer sponsored" : "sponsored";

  const media = (
    <img
      src={imgSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover"
    />
  );

  const frameClass =
    "block rounded-2xl overflow-hidden bg-[#F5EFE6] aspect-[4.8/1] shadow-[0_10px_28px_rgba(17,24,39,0.06)] card-lift";

  return (
    <section
      data-testid="home-banner-section"
      aria-label="Advertisement"
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
    >
      {linkUrl ? (
        <a
          href={linkUrl}
          target={target}
          rel={rel}
          data-testid="home-banner-link"
          className={frameClass}
        >
          {media}
        </a>
      ) : (
        <div data-testid="home-banner-static" className={frameClass}>
          {media}
        </div>
      )}
    </section>
  );
}
